import React, { useState } from 'react';
import { PhoneIcon, CheckIcon, ThumbsUpIcon, MessageCircleIcon, Share2Icon, PaperclipIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { hasContent } from '../RichTextEditor';
import { resolveHtml, resolveText } from '../../utils/variables';
import type { NoticeVariable } from '../../types/variables';
import type { ChannelsState } from '../../types/composer';
export type PreviewTab = 'push' | 'sms' | 'email' | 'voice' | 'website' | 'social';
interface NoticePreviewProps {
  channels: ChannelsState;
  title: string;
  description: string;
  categoryLabel: string;
  previewTab: PreviewTab;
  onPreviewTabChange: (tab: PreviewTab) => void;
  /** Variables used to resolve chips into real values. */
  variables: NoticeVariable[];
  /** Language the notice content is written in. */
  contentLang: 'fr' | 'en';
}
export function NoticePreview({
  channels,
  title,
  description,
  categoryLabel,
  previewTab,
  onPreviewTabChange,
  variables,
  contentLang
}: NoticePreviewProps) {
  const {
    t
  } = useLanguage();
  const [voiceTestPhone, setVoiceTestPhone] = useState('');
  const [voiceTestSent, setVoiceTestSent] = useState(false);

  /** Plain text with every variable replaced by its value. */
  const text = (html: string) => resolveText(html, variables, contentLang);
  /** Rich HTML with every variable replaced by its value. */
  const rich = (html: string) => resolveHtml(html, variables, contentLang);
  const tabs: {
    id: PreviewTab;
    label: string;
  }[] = [{
    id: 'push',
    label: t('Push', 'Push')
  }, {
    id: 'sms',
    label: t('SMS', 'SMS')
  }, {
    id: 'email',
    label: t('Courriel', 'Email')
  }, {
    id: 'voice',
    label: t('Voix', 'Voice')
  }, {
    id: 'website',
    label: t('Site web', 'Website')
  }, {
    id: 'social',
    label: t('Social', 'Social')
  }];
  const isPhone = previewTab === 'push' || previewTab === 'sms' || previewTab === 'voice';
  return <section className="space-y-4" data-id="element-3888" data-mp="eh0z07rx2rxs">
      <div className="flex items-center justify-between gap-2 flex-wrap" data-id="element-3889" data-mp="389iteb2kcuyw">
        <h2 className="text-base font-semibold text-slate-900" data-id="element-3890" data-mp="38bmwiqsn1py4">
          {t('Aperçu', 'Preview')}
        </h2>
        <div className="flex flex-wrap gap-1 bg-slate-200/50 p-1 rounded-lg" data-id="element-3891" data-mp="3dagb6mjnvmzv">
          {tabs.map(tab => <button key={tab.id} onClick={() => {
          onPreviewTabChange(tab.id);
          setVoiceTestSent(false);
        }} className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${previewTab === tab.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-3892" data-mp="3m9bfft35an0j">
              {tab.label}
            </button>)}
        </div>
      </div>

      <div className="flex justify-center" data-id="element-3893" data-mp="25u0id15d0nv5">
        {/* Phone mockup — push / sms / voice */}
        {isPhone && <div className="w-[300px] h-[600px] bg-white rounded-[40px] border-[8px] border-slate-900 overflow-hidden relative" data-id="element-3894" data-mp="2ask3izc4pvmg">
            <div className="absolute top-0 inset-x-0 h-6 flex justify-center z-20" data-id="element-3895" data-mp="2389jv3uflegq">
              <div className="w-32 h-6 bg-slate-900 rounded-b-xl" data-id="element-3896" data-mp="1ycc5h32qai6l" />
            </div>

            <div className="w-full h-full bg-slate-50 pt-12 px-4 relative" data-id="element-3897" data-mp="1tav3t5cf6roy">
              {previewTab === 'push' && <div className="bg-white/80 backdrop-blur-md rounded-2xl p-4 border border-slate-100 mt-4" data-id="element-3898" data-mp="1pcxcnwz39vix">
                  <div className="flex items-center gap-2 mb-2" data-id="element-3899" data-mp="1kdku3of93am5">
                    <img src="https://cdn.magicpatterns.com/uploads/ckK2136PdZbnMQPqZiNST7/image.png" alt={t('Logo Ville de Brossard', 'City of Brossard logo')} className="w-5 h-5 rounded object-cover flex-shrink-0" data-id="element-3900" data-mp="1fjgksyiiiqhw" />
                    <span className="text-xs font-medium text-slate-500 truncate" data-id="element-3901" data-mp="11kqin4gu60mo">
                      {t('Ville de Brossard', 'City of Brossard')}
                    </span>
                    <span className="text-xs text-slate-400 ml-auto" data-id="element-3902" data-mp="2wphmf9h5221b">
                      {t('Maintenant', 'Now')}
                    </span>
                  </div>
                  <h4 className="text-sm font-semibold text-slate-900 mb-1" data-id="element-3903" data-mp="2myv9xo4pxrmu">
                    {text(channels.push.title) || title || t('Titre de la notification', 'Notification title')}
                  </h4>
                  <p className="text-sm text-slate-600 leading-snug" data-id="element-3904" data-mp="2cz4ffmg5rcmr">
                    {text(channels.push.message) || description || t('Le message de votre notification apparaîtra ici…', 'Your notification message will appear here…')}
                  </p>
                </div>}

              {previewTab === 'sms' && <div className="mt-8 space-y-4" data-id="element-3905" data-mp="77qzznu7pdox">
                  <div className="flex justify-center" data-id="element-3906" data-mp="75ejlf43rbdz">
                    <span className="text-[10px] font-medium text-slate-400" data-id="element-3907" data-mp="hch5u4sql1yf">
                      {t("Aujourd'hui 10:42", 'Today 10:42')}
                    </span>
                  </div>
                  <div className="bg-blue-500 text-white rounded-2xl rounded-tr-sm p-3 max-w-[85%] ml-auto" data-id="element-3908" data-mp="q3zkucdy5bpx">
                    <p className="text-sm leading-snug" data-id="element-3909" data-mp="lbybozc2s144">
                      {text(channels.sms.text) || t('Votre message texte apparaîtra ici. Il doit être court et direct.', 'Your text message will appear here. Keep it short and direct.')}
                    </p>
                  </div>
                </div>}

              {previewTab === 'voice' && <div className="absolute inset-0 bg-[#0F172A] pt-14 px-5 flex flex-col" data-id="element-3910" data-mp="3atp1muy7n7l9">
                  <div className="flex flex-col items-center text-center" data-id="element-3911" data-mp="36smwjxhm41bd">
                    <span className="text-xs font-medium text-slate-400 uppercase tracking-widest mb-4" data-id="element-3912" data-mp="36ug7vq3679pp">
                      {t('Appel entrant', 'Incoming call')}
                    </span>
                    <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center mb-3" data-id="element-3913" data-mp="1eswn3e204bhf">
                      <PhoneIcon className="w-8 h-8 text-[#4F46E5]" data-id="element-3914" data-mp="1js9l8uybp4zb" />
                    </div>
                    <h4 className="text-base font-semibold text-white" data-id="element-3915" data-mp="1omx0s4bcmp1r">
                      {t('Ville de Brossard', 'City of Brossard')}
                    </h4>
                    <p className="text-xs text-slate-400 mb-4" data-id="element-3916" data-mp="1xodbye9zmv71">
                      {t('Message vocal automatisé', 'Automated voice message')}
                    </p>
                  </div>

                  <div className="bg-white/10 rounded-2xl p-3 text-left mb-4 overflow-y-auto max-h-32" data-id="element-3917" data-mp="27eg308xjtz2q">
                    <p className="text-xs text-slate-200 leading-relaxed" data-id="element-3918" data-mp="2cfvn9yrwk1k2">
                      {text(channels.voice.script) || t('Le script de votre appel vocal sera lu ici par une voix automatisée…', 'Your call script will be read here by an automated voice…')}
                    </p>
                  </div>

                  <div className="mt-auto bg-white rounded-2xl p-4 mb-4" data-id="element-3919" data-mp="3ry1wylawpx51">
                    {voiceTestSent ? <div className="text-center" data-id="element-3920" data-mp="3n0jqd1kd3u07">
                        <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-2" data-id="element-3921" data-mp="oh335jhnyudd">
                          <CheckIcon className="w-5 h-5 text-emerald-600" data-id="element-3922" data-mp="ji8hm9sbarpk" />
                        </div>
                        <p className="text-sm font-semibold text-slate-900" data-id="element-3923" data-mp="ep5mvybstc7s">
                          {t('Test envoyé !', 'Test sent!')}
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-3924" data-mp="177knp0scejnn">
                          {t('Un appel test est en route vers ', 'A test call is on its way to ')}
                          {voiceTestPhone}.
                        </p>
                        <button onClick={() => setVoiceTestSent(false)} className="mt-2 text-xs font-semibold text-[#4F46E5] hover:text-[#4338CA]" data-id="element-3925" data-mp="fkhy7gd8hlip">
                          {t('Envoyer un autre test', 'Send another test')}
                        </button>
                      </div> : <>
                        <label className="block text-xs font-semibold text-slate-700 mb-1.5" data-id="element-3926" data-mp="yhswaxn4q8wk">
                          {t("Tester l'appel vocal", 'Test the voice call')}
                        </label>
                        <input type="tel" value={voiceTestPhone} onChange={e => setVoiceTestPhone(e.target.value)} placeholder="(514) 555-0199" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5] mb-2" data-id="element-3927" data-mp="pg2wb3f6m1n6" />
                        <button onClick={() => {
                  if (voiceTestPhone.trim()) setVoiceTestSent(true);
                }} disabled={!voiceTestPhone.trim()} className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-3928" data-mp="3bpk3lbp7otoo">
                          <PhoneIcon className="w-4 h-4" data-id="element-3929" data-mp="2nzg49gj23g3h" />{' '}
                          {t('Envoyer un test', 'Send a test')}
                        </button>
                      </>}
                  </div>
                </div>}
            </div>
          </div>}

        {/* Social post mockup */}
        {previewTab === 'social' && <div className="w-full max-w-[360px] bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm" data-id="element-3930" data-mp="hn2a2305v8bc">
            <div className="flex items-center gap-3 p-3 border-b border-slate-100" data-id="element-3931" data-mp="cnhabqd62vxk">
              <img src="https://cdn.magicpatterns.com/uploads/kfqfiE7nmccz961Hy3x3v1/image.png" alt={t('Logo Ville de Brossard', 'City of Brossard logo')} className="w-9 h-9 rounded-full object-cover flex-shrink-0" data-id="element-3932" data-mp="7pxmbyqo34sf" />
              <div className="min-w-0" data-id="element-3933" data-mp="3q3ezj925zxua">
                <p className="text-sm font-semibold text-slate-900 truncate" data-id="element-3934" data-mp="3q4gidzpf9omk">
                  {t('Ville de Brossard', 'City of Brossard')}
                </p>
                <p className="text-[11px] text-slate-400" data-id="element-3935" data-mp="3g4zi5xsxw2xz">
                  {t("À l'instant · ", 'Just now · ')}
                  <span aria-hidden="true" data-id="element-3936" data-mp="3c348a4wy0h8l">🌐</span>
                </p>
              </div>
            </div>

            <div className="px-3 py-2" data-id="element-3937" data-mp="1rmd3fxdjms61">
              {hasContent(channels.social.postText) ? <div className="text-sm text-slate-700 leading-snug [&_a]:text-[#4F46E5] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_b]:font-semibold [&_strong]:font-semibold [&_img]:w-full [&_img]:rounded-lg [&_img]:my-2" dangerouslySetInnerHTML={{
            __html: rich(channels.social.postText)
          }} data-id="element-3938" data-mp="1vmf72c2u9vny" /> : <p className="text-sm text-slate-400" data-id="element-3939" data-mp="1cwp431oxa3ky">
                  {t('Le texte de votre publication apparaîtra ici.', 'Your post text will appear here.')}
                </p>}
            </div>

            <div className="flex items-center justify-around px-3 py-2 border-t border-slate-100 text-slate-500" data-id="element-3940" data-mp="1molf1dc3xo6l">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium" data-id="element-3941" data-mp="u67olyt3p4hj">
                <ThumbsUpIcon className="w-4 h-4" data-id="element-3942" data-mp="u44rkutj6b3b" /> {t("J'aime", 'Like')}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium" data-id="element-3943" data-mp="m3f68hxh7d5u">
                <MessageCircleIcon className="w-4 h-4" data-id="element-3944" data-mp="i3o79qxspqq8" /> {t('Commenter', 'Comment')}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium" data-id="element-3945" data-mp="vujih9vrojwz">
                <Share2Icon className="w-4 h-4" data-id="element-3946" data-mp="qwrsileon7vf" /> {t('Partager', 'Share')}
              </span>
            </div>
          </div>}

        {/* Desktop mockup — email / website */}
        {(previewTab === 'email' || previewTab === 'website') && <div className="w-full max-w-[360px]" data-id="element-3947" data-mp="d5cxfg7i3g80">
            <div className="bg-white rounded-xl border border-slate-300 overflow-hidden shadow-sm" data-id="element-3948" data-mp="d64tst8x9hzc">
              <div className="h-9 bg-slate-100 border-b border-slate-200 flex items-center gap-1.5 px-3" data-id="element-3949" data-mp="84firdlp94b9">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400" data-id="element-3950" data-mp="1om3a9i6fj6al" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400" data-id="element-3951" data-mp="1jk45q4yye3c2" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" data-id="element-3952" data-mp="31i96c2hlary5" />
                <div className="ml-2 flex-1 h-5 bg-white rounded border border-slate-200 text-[9px] text-slate-400 flex items-center px-2 truncate" data-id="element-3953" data-mp="36hlsel0mzuzo">
                  {previewTab === 'email' ? 'mail.brossard.ca' : 'brossard.ca'}
                </div>
              </div>

              <div className="bg-white h-[440px] overflow-y-auto relative" data-id="element-3954" data-mp="2xh72178tfppb">
                {previewTab === 'email' && <div className="bg-white" data-id="element-3955" data-mp="3p83ggcaiy2ep">
                    <div className="px-5 py-3 border-b border-slate-100" data-id="element-3956" data-mp="3p6tgfhk42k5f">
                      <p className="text-xs text-slate-500 mb-1" data-id="element-3957" data-mp="3bh86ewoahf9j">
                        {t('De: Ville de Brossard', 'From: City of Brossard')}
                      </p>
                      <h4 className="text-sm font-semibold text-slate-900" data-id="element-3958" data-mp="3gdgig176wsch">
                        {text(channels.email.subject) || title || t('Objet du courriel', 'Email subject')}
                      </h4>
                    </div>
                    <div className="p-6" data-id="element-3959" data-mp="2k86l00q39xks">
                      <img src="https://cdn.magicpatterns.com/uploads/nedc6b9KaGTagFGJXbwvij/pasted-image.png" alt={t('Logo Ville de Brossard', 'City of Brossard logo')} className="h-8 w-auto object-contain mb-5" data-id="element-3960" data-mp="2fawyotxzy216" />

                      <h1 className="text-xl font-bold text-slate-900 mb-3" data-id="element-3961" data-mp="32zplo0wn4zjn">
                        {title || t("Titre de l'avis", 'Notice title')}
                      </h1>

                      {hasContent(channels.email.body) ? <div className="text-sm text-slate-600 leading-relaxed [&_a]:text-[#4F46E5] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_b]:font-semibold [&_strong]:font-semibold [&_img]:w-full [&_img]:rounded-lg [&_img]:my-3" dangerouslySetInnerHTML={{
                  __html: rich(channels.email.body)
                }} data-id="element-3962" data-mp="2u6ckycsc4tn0" /> : <p className="text-sm text-slate-400 leading-relaxed" data-id="element-3963" data-mp="3pbiwmqcnexo1">
                          {description || t("Le contenu détaillé de votre courriel s'affichera ici.", 'The detailed content of your email will appear here.')}
                        </p>}

                      {channels.email.attachments.length > 0 && <div className="mt-5 pt-4 border-t border-slate-100" data-id="element-3964" data-mp="h065vhp3hfpl">
                          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2" data-id="element-3965" data-mp="ltinh7vcwm8u">
                            {channels.email.attachments.length}{' '}
                            {t('pièce(s) jointe(s)', 'attachment(s)')}
                          </p>
                          <ul className="space-y-1.5" data-id="element-3966" data-mp="zrgxk12n9wij">
                            {channels.email.attachments.map(file => <li key={file.id} className="flex items-center gap-2 border border-slate-200 rounded-md px-2.5 py-1.5" data-id="element-3967" data-mp="2vvwq18rtf6fz">
                                <PaperclipIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-3968" data-mp="35n16kcx52zcx" />
                                <span className="text-xs text-slate-600 truncate" data-id="element-3969" data-mp="30sn4xsrvvvzt">
                                  {file.name}
                                </span>
                              </li>)}
                          </ul>
                        </div>}
                    </div>
                  </div>}

                {previewTab === 'website' && <div className="relative" data-id="element-3970" data-mp="1u5hwc0l55w4v">
                    <img src="https://cdn.magicpatterns.com/uploads/paDPkCcwNHTw4rcW21cM4q/image.png" alt={t('Aperçu du site web de Brossard', 'Preview of the Brossard website')} className="w-full block" data-id="element-3971" data-mp="1u69thfue01x9" />
                    <div className="absolute top-[13%] inset-x-0 bg-[#E7F1FB] px-3 py-3 border-y border-[#cfe0f5]" data-id="element-3972" data-mp="3s2rm8iyz9mho">
                      <div className="flex items-start justify-between gap-2" data-id="element-3973" data-mp="3w28ufk5sgxqj">
                        <div className="min-w-0" data-id="element-3974" data-mp="3i3iw6178gs0l">
                          <span className="inline-block px-3 py-0.5 rounded-full bg-[#1D4ED8] text-white text-[10px] font-bold mb-1.5" data-id="element-3975" data-mp="3i65627hljjlz">
                            {categoryLabel}
                          </span>
                          <p className="text-[11px] font-bold text-slate-900 leading-snug" data-id="element-3976" data-mp="3d4plshn8th4n">
                            {text(channels.website.bannerText) || title || t('Le texte de votre bannière apparaîtra ici', 'Your banner text will appear here')}
                          </p>
                          <p className="text-[10px] font-semibold text-slate-800 underline mt-1" data-id="element-3977" data-mp="3qbbqoumg2082">
                            {text(channels.website.ctaLabel) || t("Consulter l'avis en cours", 'View the current notice')}
                          </p>
                        </div>
                        <span className="text-slate-500 text-sm leading-none" data-id="element-3978" data-mp="3cjnd0mwjr3oz">✕</span>
                      </div>
                    </div>
                  </div>}
              </div>
            </div>

            <div className="mx-auto h-2.5 w-[88%] bg-slate-300 rounded-b-xl" data-id="element-3979" data-mp="32ky77shxd9p3" />
            <div className="mx-auto h-1 w-[40%] bg-slate-200 rounded-b-lg" data-id="element-3980" data-mp="2xmwo57ki1adm" />
          </div>}
      </div>
    </section>;
}